import { AsyncPipe, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LoadingIndicatorDirective } from '@zero/shared/ui-directives';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { FileToUpload, TicketCommentToSubmit, TicketToSubmit } from '@zero/shared/util-models';
import { TicketDialogService, UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { TicketInfrastructure, ZendeskTopic } from '@zero/tickets/domain';
import { MAX_UPLOAD_FILE_SIZE } from 'constants/shared-constants';
import { Observable, firstValueFrom, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { TicketCreationState } from '../../models/ticket-creation-state';
import { HasTopicHintPipe } from '../../pipes/has-topic-hint/has-topic-hint.pipe';
import { UploadInProgressPipe } from '../../pipes/upload-in-progress/upload-in-progress.pipe';

@Component({
  selector: 'tickets-create-ticket',
  templateUrl: './create-ticket.component.html',
  imports: [
    FormsModule,
    LoadingIndicatorDirective,
    NgStyle,
    AsyncPipe,
    HasTopicHintPipe,
    UploadInProgressPipe,
    TranslocoPipe,
    ButtonComponent,
  ],
})
export class CreateTicketComponent {
  @Input() public ticketRef: string;
  @Output() public readonly ticketCreationState = new EventEmitter<TicketCreationState>();

  readonly #ticketDialogService = inject(TicketDialogService);
  readonly #msgService = inject(UserMessagesHolderService);
  readonly #ticketInfrastructure = inject(TicketInfrastructure);
  readonly #translateService = inject(TranslocoService);

  public files: FileToUpload[] = [];
  public errors: string[] = [];
  public ongoingRequest = false;
  public ticketText: string = null;

  public readonly topics$: Observable<ZendeskTopic[]> = this.#ticketInfrastructure.getZendeskTopics();

  public submitForm(f: NgForm): void {
    this.errors = [];
    this.#msgService.clearAllMessages();

    if (f.valid && !new UploadInProgressPipe().transform(this.files)) {
      if (this.ticketRef) {
        this.#submitComment(f, this.ticketRef);
      } else {
        this.#submitTicket(f);
      }
    }
  }

  public submitFileUpload(fileUpload: NgForm, fileUploadElem: HTMLInputElement): void {
    this.errors = [];
    this.#msgService.clearAllMessages();

    if (fileUploadElem.files.length > 2) {
      this.errors.push('Bitte wähle maximal 2 Dateien aus');
      fileUpload.reset('tempFiles');
      return;
    }

    const filesToUpload: FileToUpload[] = Array.from(fileUploadElem.files).map((file) => ({
      fileData: file,
      uploadInProgress: false,
      uploadProgress: 0,
      fileName: file.name,
      fileRefForTicket: undefined,
      uploadError: file.size > MAX_UPLOAD_FILE_SIZE ? 'tickets.create.attachments.max-size' : undefined,
    }));

    filesToUpload.forEach((fileToUpload) => {
      this.files.push(fileToUpload);
      if (!fileToUpload.uploadError) {
        this.#ticketDialogService
          .upload(fileToUpload)
          .pipe(filter((result) => !result.success))
          .subscribe((result) => {
            if (result.message) {
              this.errors.push(result.message);
            } else {
              this.errors.push(`Datei ${fileToUpload.fileName} konnte nicht hochgeladen werden.`);
            }
          });
      }
    });

    fileUpload.reset('tempFiles');
  }

  #submitTicket(f: NgForm): void {
    const ticketData: TicketToSubmit = {
      ticketSubject: f.value.ticketSubject,
      ticketText: this.ticketText,
      ticketTopic: f.value.ticketTopic,
      attachedFilesRefs: this.files.map((file) => file.fileRefForTicket),
    };
    this.ongoingRequest = true;
    this.#ticketDialogService.submitTicket(ticketData).subscribe((result) => {
      if (result.success) {
        this.ticketCreationState.emit({ status: 'finished', errors: [], ongoingRequest: false });
      } else {
        this.errors.push(result.message);
      }
      this.ongoingRequest = false;
    });
  }

  #submitComment(f: NgForm, ticketRef: string): void {
    const commentData: TicketCommentToSubmit = {
      ticketId: ticketRef,
      commentText: this.ticketText,
      attachedFilesRefs: this.files.map((file) => file.fileRefForTicket),
    };

    this.errors = [];
    this.ongoingRequest = true;

    this.#ticketDialogService
      .updateTicketStatus(ticketRef, 'Open')
      .pipe(
        switchMap(async (result) => {
          if (result.success) {
            const resultSubmitComment = await firstValueFrom(this.#ticketDialogService.submitComment(commentData));
            this.ongoingRequest = false;
            if (resultSubmitComment.success) {
              this.ticketCreationState.emit({ status: 'finished', errors: [], ongoingRequest: false });
              return of({
                success: true,
              });
            } else {
              this.errors.push(resultSubmitComment.message);
              this.ticketCreationState.emit({ status: 'finished', errors: this.errors, ongoingRequest: false });
              return of({
                success: false,
                message: 'Fehler beim Speichern der Antwort.',
              });
            }
          } else {
            return of({
              success: false,
              message: 'Fehler beim Speichern der Antwort.',
            });
          }
        }),
      )
      .subscribe();
  }

  public cancel(): void {
    this.ticketCreationState.emit({ status: 'canceled', errors: [], ongoingRequest: false });
    this.#msgService.clearAllMessages();
  }

  public deleteUploadedFile(fileToDelete: FileToUpload): void {
    this.#ticketDialogService.delete(fileToDelete).subscribe(() => {
      this.errors = [];
      this.#msgService.clearAllMessages();

      // whether successful or not, we always remove the file from the dialog
      this.files = this.files.filter((file) => file.fileRefForTicket !== fileToDelete.fileRefForTicket);
    });
  }

  public handleTopicChange(ev: Event): void {
    const tag = (ev?.target as HTMLSelectElement)?.value;
    tag && this.#prefillTicketText(tag);
  }

  #prefillTicketText = (tag: string): void => {
    const key = `tickets.create.topic.prefilled-text.${tag}`;
    const prefilledTicketText = this.#translateService.translate(key);
    this.ticketText = prefilledTicketText !== key ? prefilledTicketText : this.ticketText;
  };
}
